/**
 * Riemann sum computation and rectangle geometry for the calculus demo.
 *
 * - `riemannSum`: computes the approximate definite integral using the chosen rule.
 * - `riemannRects`: returns per-subinterval geometry data for the render layer.
 *
 * Rules:
 * - `left`  : sample at the left endpoint of each subinterval
 * - `right` : sample at the right endpoint
 * - `mid`   : sample at the midpoint
 * - `trap`  : trapezoid rule — average of left and right endpoint heights
 */

export type RiemannRule = 'left' | 'right' | 'mid' | 'trap';

export interface RiemannRect {
  /** Left world-x of the subinterval. */
  x0: number;
  /** Right world-x of the subinterval. */
  x1: number;
  /**
   * Representative height for this subinterval.
   * - `left` / `right` / `mid`: the single sampled function value.
   * - `trap`: the left-endpoint height; `heightRight` carries the right-endpoint height.
   */
  height: number;
  /**
   * Right-endpoint height — only defined for `'trap'` rule so the bar layer can
   * slant the top edge of the trapezoid.
   */
  heightRight?: number;
}

/**
 * Compute the Riemann sum approximation of ∫ₐᵇ f(x) dx with `n` subintervals.
 *
 * Signed areas are handled naturally: subintervals where the height is negative
 * contribute a negative area, so `∫₀²π sin = 0`.
 */
export function riemannSum(
  f: (x: number) => number,
  a: number,
  b: number,
  n: number,
  rule: RiemannRule,
): number {
  const dx = (b - a) / n;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const xi = a + i * dx;
    const xi1 = a + (i + 1) * dx;
    switch (rule) {
      case 'left':
        sum += f(xi);
        break;
      case 'right':
        sum += f(xi1);
        break;
      case 'mid':
        sum += f(xi + dx * 0.5);
        break;
      case 'trap':
        sum += (f(xi) + f(xi1)) * 0.5;
        break;
    }
  }
  return sum * dx;
}

/**
 * Return per-subinterval geometry for the Riemann bar render layer.
 *
 * Each entry maps to one subinterval `[x0, x1]`. For `'trap'`, `heightRight` is
 * set to the right-endpoint value so the render layer can draw a slanted top.
 * For all other rules, `heightRight` is absent.
 */
export function riemannRects(
  f: (x: number) => number,
  a: number,
  b: number,
  n: number,
  rule: RiemannRule,
): RiemannRect[] {
  const dx = (b - a) / n;
  const rects: RiemannRect[] = [];
  for (let i = 0; i < n; i++) {
    const x0 = a + i * dx;
    const x1 = a + (i + 1) * dx;
    switch (rule) {
      case 'left':
        rects.push({ x0, x1, height: f(x0) });
        break;
      case 'right':
        rects.push({ x0, x1, height: f(x1) });
        break;
      case 'mid':
        rects.push({ x0, x1, height: f(x0 + dx * 0.5) });
        break;
      case 'trap':
        rects.push({ x0, x1, height: f(x0), heightRight: f(x1) });
        break;
    }
  }
  return rects;
}
